import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Col, Form, Row, Table, Spinner, Button } from 'react-bootstrap';
import Select from 'react-select';
import { IoMdClose } from 'react-icons/io';
import debounce from 'lodash/debounce';
import { toWords } from 'number-to-words';
import CommonDcFields from '../../DC/CommonDcFields';
import { BASE_URL } from '../../../services/Api';
import { authHeader } from '../../../services/auth-header';

function DeliveryChallanPDUpdate() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [formData, setFormData] = useState({
        sono: '',
        sodate: '',
        buyerorderno: '',
        buyerorderdate: '',
        deliverynoteno: '',
        deliverynotedate: '',
        destination: '',
        dispatchthrough: '',
        reference: '',
        dispatchdocno: '',
        termsofdelivery: '',
        packingandforwardingpercent: 0,
        freightpercent: 0,
        gstpercent: 18
    });
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productOptions, setProductOptions] = useState([]);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));
    const [rows, setRows] = useState([]);
    const [commonFormData, setCommonFormData] = useState({});
    const [calculationSummary, setCalculationSummary] = useState({
        nettotal: 0,
        pAndF: 0,
        freight: 0,
        subtotal: 0,
        gst: 0,
        grandtotalinr: 0
    });

    const unitOptions = [
        { value: "", label: "Select UOM" },
        { value: "bot", label: "Bot" },
        { value: "ctn", label: "Ctn" },
        { value: "days", label: "Days" },
        { value: "ft", label: "Ft" },
        { value: "hrs", label: "Hrs" },
        { value: "kg", label: "Kg" },
        { value: "ltr", label: "Ltr" },
        { value: "mtr", label: "Mtr" },
        { value: "nos", label: "Nos" },
        { value: "pil", label: "Pil" },
        { value: "pkt", label: "Pkt" },
        { value: "set", label: "Set" },
        { value: "pcs", label: "Pcs" }
    ];


    const [status, setstatus] = useState('')

    const fetchDeliveryChallanData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}deliveryChallan/findById/${id}`, {
                headers: authHeader()
            });

            const data = response.data;
            setstatus(data.status);
            setFormData({
                sono: data.sono || '',
                sodate: data.sodate || '',
                buyerorderno: data.buyerorderno || '',
                buyerorderdate: data.buyerorderdate || '',
                deliverynoteno: data.deliverynoteno || '',
                deliverynotedate: data.deliverynotedate || '',
                destination: data.destination || '',
                dispatchthrough: data.dispatchthrough || '',
                reference: data.reference || '',
                dispatchdocno: data.dispatchdocno || '',
                termsofdelivery: data.termsofdelivery || '',
                packingandforwardingpercent: data.packingandforwardingpercent || 0,
                freightpercent: data.freightpercent || 0,
                gstpercent: data.gstpercent || 18
            });

            if (data.deliveryChallanItems && data.deliveryChallanItems.length > 0) {
                setRows(data.deliveryChallanItems.map(item => ({
                    id: item.id,
                    product: { id: item.product?.id },
                    description: item.product?.description || '-',
                    productremarks: item.productremarks || "",
                    quantity: item.quantity || 0,
                    unitrate: item.unitrate || 0,
                    amount: item.amount || 0,
                    hsnsacno: item.hsnsacno || "",
                    partno: item.partno || "",
                    serialnumber: item.serialnumber || "",
                    modelnumber: item.modelnumber || "",
                    tagnumber: item.tagnumber || "",
                    moc: item.moc || "",
                    itemcode: item.itemcode || "",
                    uom: item.uom || "",
                    value: item.product?.id
                })));
            }

            // Set common form data
            setCommonFormData({
                customer: { value: data.customer?.id, label: data.customer?.companyname },
                customerDetails: {
                    contactno: data.contactno,
                    contactperson: data.contactperson,
                    email: data.email,
                    paymentterm: data.customer?.paymentterm,
                    freight: data.customer?.freight,
                    packingandforwarding: data.customer?.packingandforwarding
                },
                customerMultipleAddress: {
                    value: data.customeraddress?.id,
                    label: `${data.customeraddress?.customeraddress}, ${data.customeraddress?.city?.cityname}`
                },
                branch: data.branch ? { value: data.branch.id, label: data.branch.branchname } : null,
                warehouse: data.warehouse ? { value: data.warehouse.id, label: data.warehouse.warehousename } : null,
                shipTo: data.shipto ? {
                    value: data.shipto.id,
                    label: `${data.shipto.customeraddress}, ${data.shipto.city?.cityname}`
                } : null,
                billTo: data.billto ? {
                    value: data.billto.id,
                    label: `${data.billto.customeraddress}, ${data.billto.city?.cityname}`
                } : null
            });

        } catch (error) {
            console.error("Error fetching DeliveryChallan data", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to fetch DeliveryChallan data. Please try again.",
            });
            navigate("/dcindex/1");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDeliveryChallanData();
    }, [id]);

    useEffect(() => {
        calculateTotals();
    }, [rows, formData.packingandforwardingpercent, formData.freightpercent, formData.gstpercent]);

    const formatNumberWithCommas = (num) => {
        if (num === null || num === undefined) return '0.00';
        return parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

   const numberToWordsWithRupees = (num) => {
     const amount = Math.round(num);
     const words = toWords(amount);
 
     // Capitalize first letter of each word
     const capitalizedWords = words.split(' ').map(word =>
       word.charAt(0).toUpperCase() + word.slice(1)
     ).join(' ');
 
     return `${capitalizedWords} Rupees Only`;
   };

    const handleCommonFormData = (data) => {
        setCommonFormData(data);
        if (data.customerDetails) {
            setFormData(prev => ({
                ...prev,
                freightpercent: parseFloat(data.customerDetails.freight) || 0,
                packingandforwardingpercent: parseFloat(data.customerDetails.packingandforwarding) || 0
            }));
        }
    };
    console.log(commonFormData)

    const calculateTotals = () => {
        const nettotal = rows.reduce((sum, row) => {
            const quantity = parseFloat(row.quantity) || 0;
            const unitrate = parseFloat(row.unitrate) || 0;
            return sum + (quantity * unitrate);
        }, 0);

        const pAndF = nettotal * (formData.packingandforwardingpercent / 100);
        const freight = nettotal * (formData.freightpercent / 100);
        const subtotal = nettotal + pAndF + freight;
        const gst = subtotal * (formData.gstpercent / 100);
        const grandtotalinr = subtotal + gst;

        setCalculationSummary({
            nettotal,
            pAndF,
            freight,
            subtotal,
            gst,
            grandtotalinr
        });
    };

    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(encodeURIComponent(inputValue || " "));
    }, 300);

    const getProductData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );

            const products = response.data?.Index || response.data?.data || [];
            const mapped = products.map(product => ({
                value: product.id,
                label: `${product.description}  - ${product.serialnumber} - ${product.partno}`,
                description: product.description,
                itemcode: product.itemcode,
                hsnsacno: product.hsnsacno,
                partno: product.partno,
                serialnumber: product.serialnumber,
                modelnumber: product.modelnumber,
                tagnumber: product.tagnumber,
                moc: product.moc,
                uom: product.unitofmeasurement || unitOptions[0].value,
            }));

            setProductOptions(mapped);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    useEffect(() => {
        getProductData();
    }, [productSearch]);

    const handleProductChange = (selectedOption) => {
        if (!selectedOption) return;
        setSelectedProduct(selectedOption);

        const newRow = {
            description: selectedOption.description,
            value: selectedOption.value,
            label: selectedOption.label,
            quantity: '',
            unitrate: '',
            amount: 0,
            partno: selectedOption.partno,
            hsnsacno: selectedOption.hsnsacno,
            itemcode: selectedOption.itemcode,
            serialnumber: selectedOption.serialnumber,
            modelnumber: selectedOption.modelnumber,
            tagnumber: selectedOption.tagnumber,
            moc: selectedOption.moc,
            uom: selectedOption.uom || unitOptions[0].value,
            productremarks: "",
        };

        setRows([...rows, newRow]);
    };

    const handleRowChange = (index, key, value) => {
        const updatedRows = [...rows];
        updatedRows[index][key] = value;

        if (key === 'quantity' || key === 'unitrate') {
            const quantity = parseFloat(updatedRows[index].quantity) || 0;
            const unitrate = parseFloat(updatedRows[index].unitrate) || 0;
            updatedRows[index].amount = quantity * unitrate;
        }

        setRows(updatedRows);
    };

    const removeRow = (index) => {
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePercentageChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: parseFloat(value) || 0
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const {
            customer,
            customerDetails,
            customerMultipleAddress,
            branch,
            warehouse,
            shipTo,
            billTo,
        } = commonFormData;

        if (!customer || !customerMultipleAddress) {
            Swal.fire({
                icon: 'warning',
                title: 'Please fill the required details!',
                text: 'All fields marked with an asterisk are required.',
            });
            return;
        }

        const deliveryChallanItems = rows.map(row => ({
            id: row.id || null,
            product: { id: row.value },
            quantity: row.quantity,
            unitrate: row.unitrate,
            amount: row.amount,
            itemcode: row.itemcode || '',
            hsnsacno: row.hsnsacno || '',
            partno: row.partno || '',
            uom: row.uom || '',
            serialnumber: row.serialnumber || '',
            modelnumber: row.modelnumber || '',
            tagnumber: row.tagnumber || '',
            productremarks: row.productremarks || '',
            moc: row.moc || '',
        }));

        const updateData = {
            id: id,
            status,
            deliveryChallanItems,
            deliverychallantype: "Pump Spare",
            deliverychallancategory: "Domestic",
            customer: { id: customer.value },
            contactno: customerDetails.contactno,
            contactperson: customerDetails.contactperson,
            email: customerDetails.email,
            customeraddress: { id: customerMultipleAddress?.value },
            shipto: { id: shipTo?.value },
            billto: { id: billTo?.value },
            branch: branch ? { id: commonFormData.branch.value } : null,
            warehouse: warehouse ? { id: commonFormData.warehouse.value } : null,
            dispatchdocno: formData.dispatchdocno,
            termsofdelivery: formData.termsofdelivery,
            sono: formData.sono,
            sodate: formData.sodate,
            buyerorderno: formData.buyerorderno,
            buyerorderdate: formData.buyerorderdate,
            deliverynoteno: formData.deliverynoteno,
            deliverynotedate: formData.deliverynotedate,
            destination: formData.destination,
            dispatchthrough: formData.dispatchthrough,
            reference: formData.reference,
            packingandforwardingpercent: formData.packingandforwardingpercent,
            freightpercent: formData.freightpercent,
            gstpercent: formData.gstpercent,
            nettotal: calculationSummary.nettotal,
            grandtotalinr: calculationSummary.grandtotalinr,
            freight: customerDetails.freight,
            packingandforwarding: customerDetails.packingandforwarding,
            paymentterm: customerDetails.paymentterm
        };

        try {
            await axios.put(`${BASE_URL}deliveryChallan/update/${id}`, updateData, {
                headers: authHeader()
            });
            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'DeliveryChallan  updated successfully!'
            });
            navigate('/dcindex/1');
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to update DeliveryChallan .'
            });
        }
    };

    if (isLoading) {
        return (
            <div className="container text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Loading DeliveryChallan  data...</p>
            </div>
        );
    }

    return (
        <div className="container">
            <Form onSubmit={handleSubmit}>

                <Row className="mb-3">
                    {/* DC DETAILS */}
                    <Col md={6}>
                        <Form.Group controlId="deliverynoteno">
                            <Form.Label>Delivery Note Number</Form.Label>
                            <Form.Control
                                type="text"
                                name="deliverynoteno"
                                value={formData.deliverynoteno}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="deliverynotedate">
                            <Form.Label>Delivery Note Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="deliverynotedate"
                                value={formData.deliverynotedate}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>

                    {/* Sales ORDER DETAILS */}
                    <Col md={6}>
                        <Form.Group controlId="sono">
                            <Form.Label>Sales Order No.</Form.Label>
                            <Form.Control
                                type="text"
                                name="sono"
                                value={formData.sono}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="sodate">
                            <Form.Label>Sales Order Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="sodate"
                                value={formData.sodate}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>

                    {/* BUYER ORDER DETAILS */}
                    <Col md={6}>
                        <Form.Group controlId="buyerorderno">
                            <Form.Label>Buyer Order No.</Form.Label>
                            <Form.Control
                                type="text"
                                name="buyerorderno"
                                value={formData.buyerorderno}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="buyerorderdate">
                            <Form.Label>Buyer Order Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="buyerorderdate"
                                value={formData.buyerorderdate}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>

                    {/* DISPATCH INFO */}
                    <Col md={6}>
                        <Form.Group controlId="dispatchthrough">
                            <Form.Label>Dispatch Through</Form.Label>
                            <Form.Control
                                type="text"
                                name="dispatchthrough"
                                value={formData.dispatchthrough}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="destination">
                            <Form.Label>Destination</Form.Label>
                            <Form.Control
                                type="text"
                                name="destination"
                                value={formData.destination}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    {/* Common Fields */}
                    <CommonDcFields
                        onDataChange={handleCommonFormData}
                        initialData={commonFormData}
                    />
                </Row>

                <Row>
                    {/* ADDITIONAL INFO */}
                    <Col md={6}>
                        <Form.Group controlId="dispatchdocno">
                            <Form.Label>Dispatch Doc No.</Form.Label>
                            <Form.Control
                                type="text"
                                name="dispatchdocno"
                                value={formData.dispatchdocno}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group controlId="termsofdelivery">
                            <Form.Label>Delivery Term</Form.Label>
                            <Form.Control
                                type="text"
                                name="termsofdelivery"
                                value={formData.termsofdelivery}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={12}>
                        <Form.Group controlId="reference">
                            <Form.Label>Other Reference</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="reference"
                                value={formData.reference}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group controlId="productSelect">
                    <Form.Label>Select a Product</Form.Label>
                    <Select
                        value={selectedProduct}
                        onInputChange={handleProductSearchChange}
                        onChange={handleProductChange}
                        options={productOptions}
                        placeholder="Search or select product"
                        isClearable
                    />
                </Form.Group>

                <div className="table-responsive mt-3">
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Product Description</th>
                                <th>Serial No.</th>
                                <th>Model No.</th>
                                <th>Tag No.</th>
                                <th>Part No.</th>
                                <th>MOC</th>
                                <th>HSN Code</th>
                                <th>Product Code</th>
                                <th>Quantity</th>
                                <th>UOM</th>
                                <th>Unit Rate</th>
                                <th>Amount</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        {row.description}
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={row.productremarks}
                                            onChange={(e) => handleRowChange(index, 'productremarks', e.target.value)}
                                            placeholder="Remarks"
                                        />
                                    </td>
                                    <td>{row.serialnumber}</td>
                                    <td>{row.modelnumber}</td>
                                    <td>{row.tagnumber}</td>
                                    <td>{row.partno}</td>
                                    <td>{row.moc}</td>
                                    <td>{row.hsnsacno}</td>
                                    <td>{row.itemcode}</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            value={row.quantity}
                                            onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <Select
                                            className='uom'
                                            value={unitOptions.find(option => option.value === row.uom)}
                                            onChange={(selected) => handleRowChange(index, 'uom', selected.value)}
                                            options={unitOptions}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: base => ({ ...base, zIndex: 9999 })
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            value={row.unitrate}
                                            onChange={(e) => handleRowChange(index, 'unitrate', e.target.value)}
                                        />
                                    </td>
                                    <td>{formatNumberWithCommas(row.amount)}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            onClick={() => removeRow(index)}
                                        >
                                            <IoMdClose />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                <hr />
                <br />
                {/* <div className="table-responsive mt-3">
                    <h4>Amount Calculation</h4>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Description</th>
                                <th>Percentage</th>
                                <th>Amount (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Net Total</td>
                                <td>-</td>
                                <td>{formatNumberWithCommas(calculationSummary.nettotal)}</td>
                            </tr>
                            <tr>
                                <td>Packing & Forwarding % :</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        name="packingandforwardingpercent"
                                        value={formData.packingandforwardingpercent}
                                        onChange={handlePercentageChange}
                                        style={{ width: '80px' }}
                                    />
                                    {commonFormData.customerDetails?.packingandforwarding &&
                                        <small className="text-muted"> (From customer: {commonFormData.customerDetails.packingandforwarding}%)</small>
                                    }
                                </td>
                                <td>{formatNumberWithCommas(calculationSummary.pAndF)}</td>
                            </tr>
                            <tr>
                                <td>Freight % :</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        name="freightpercent"
                                        value={formData.freightpercent}
                                        onChange={handlePercentageChange}
                                        style={{ width: '80px' }}
                                    />
                                    {commonFormData.customerDetails?.freight &&
                                        <small className="text-muted"> (From customer: {commonFormData.customerDetails.freight}%)</small>
                                    }
                                </td>
                                <td>{formatNumberWithCommas(calculationSummary.freight)}</td>
                            </tr>
                            <tr>
                                <td>Sub Total :</td>
                                <td>-</td>
                                <td>{formatNumberWithCommas(calculationSummary.subtotal)}</td>
                            </tr>
                            <tr>
                                <td>GST % :</td>
                                <td>
                                    <Form.Control
                                        type="number"
                                        name="gstpercent"
                                        value={formData.gstpercent}
                                        onChange={handlePercentageChange}
                                        style={{ width: '80px' }}
                                    />
                                </td>
                                <td>{formatNumberWithCommas(calculationSummary.gst)}</td>
                            </tr>
                            <tr>
                                <td><strong>Grand Total :</strong></td>
                                <td>-</td>
                                <td><strong>{formatNumberWithCommas(calculationSummary.grandtotalinr)}</strong></td>
                            </tr>
                            <tr>
                                <td colSpan="2"><strong>Grand Total in Words</strong></td>
                                <td><em>{numberToWordsWithRupees(calculationSummary.grandtotalinr)}</em></td>
                            </tr>
                        </tbody>
                    </Table>
                </div> */}

                <div className="card mt-3">
                    <div className="card-header bg-light">
                        <h4 className="mb-0">Amount Calculation</h4>
                    </div>
                    <div className="card-body p-0">
                        <div className="list-group list-group-flush">
                            {/* Net Total */}
                            <div className="list-group-item">
                                <div className="row align-items-center">
                                    <div className="col-md-6">Net Total</div>
                                    <div className="col-md-2">-</div>
                                    <div className="col-md-4 text-end">{formatNumberWithCommas(calculationSummary.nettotal)}</div>
                                </div>
                            </div>

                            {/* Packing & Forwarding */}
                            <div className="list-group-item">
                                <div className="row align-items-center">
                                    <div className="col-md-6">Packing & Forwarding %</div>
                                    <div className="col-md-2">
                                        <div className="d-flex align-items-center">
                                            <Form.Control
                                                type="number"
                                                name="packingandforwardingpercent"
                                                value={formData.packingandforwardingpercent}
                                                onChange={handlePercentageChange}
                                                className="me-2"
                                                style={{ width: '80px' }}
                                            />
                                            {commonFormData.customerDetails?.packingandforwarding && (
                                                <small className="text-muted">
                                                    (Customer: {commonFormData.customerDetails.packingandforwarding}%)
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-4 text-end">{formatNumberWithCommas(calculationSummary.pAndF)}</div>
                                </div>
                            </div>

                            {/* Freight */}
                            <div className="list-group-item">
                                <div className="row align-items-center">
                                    <div className="col-md-6">Freight %</div>
                                    <div className="col-md-2">
                                        <div className="d-flex align-items-center">
                                            <Form.Control
                                                type="number"
                                                name="freightpercent"
                                                value={formData.freightpercent}
                                                onChange={handlePercentageChange}
                                                className="me-2"
                                                style={{ width: '80px' }}
                                            />
                                            {commonFormData.customerDetails?.freight && (
                                                <small className="text-muted">
                                                    (Customer: {commonFormData.customerDetails.freight}%)
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-4 text-end">{formatNumberWithCommas(calculationSummary.freight)}</div>
                                </div>
                            </div>

                            {/* Sub Total */}
                            <div className="list-group-item">
                                <div className="row align-items-center">
                                    <div className="col-md-6">Sub Total</div>
                                    <div className="col-md-2">-</div>
                                    <div className="col-md-4 text-end">{formatNumberWithCommas(calculationSummary.subtotal)}</div>
                                </div>
                            </div>

                            {/* GST */}
                            <div className="list-group-item">
                                <div className="row align-items-center">
                                    <div className="col-md-6">GST %</div>
                                    <div className="col-md-2">
                                        <Form.Control
                                            type="number"
                                            name="gstpercent"
                                            value={formData.gstpercent}
                                            onChange={handlePercentageChange}
                                            style={{ width: '80px' }}
                                        />
                                    </div>
                                    <div className="col-md-4 text-end">{formatNumberWithCommas(calculationSummary.gst)}</div>
                                </div>
                            </div>

                            {/* Grand Total */}
                            <div className="list-group-item bg-light">
                                <div className="row align-items-center">
                                    <div className="col-md-6"><strong>Grand Total</strong></div>
                                    <div className="col-md-2">-</div>
                                    <div className="col-md-4 text-end">
                                        <strong>{formatNumberWithCommas(calculationSummary.grandtotalinr)}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* Grand Total in Words */}
                            <div className="list-group-item">
                                <div className="row">
                                    <div className="col-md-8"><strong>Grand Total in Words</strong></div>
                                    <div className="col-md-4 text-end">
                                        <em>{numberToWordsWithRupees(calculationSummary.grandtotalinr)}</em>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between mt-3">
                    <Button
                        variant="outline-secondary"
                        onClick={() => navigate(-1)}
                    >
                        <i className="bi bi-arrow-left me-2"></i>BACK
                    </Button>
                    <Button variant="primary" type="submit">
                        UPDATE Delivery Challan
                    </Button>
                </div>
            </Form>
        </div>
    );
}

export default DeliveryChallanPDUpdate;